<template>
    <div
        class="el-form-item"
        :class="[{
            'el-form-item--feedback': elForm && elForm.statusIcon,
            'is-error': validateState === 'error',
            'is-validating': validateState === 'validating',
            'is-success': validateState === 'success',
            'is-required': isRequired || required,
            'is-warning': validateState === 'warning',
    },
    sizeClass ? 'el-form-item--' + sizeClass : ''
  ]"
    >
        <label
            :for="labelFor"
            class="el-form-item__label"
            v-bind:style="labelStyle"
            v-if="label || $slots.label"
        >
            <slot name="label">{{label + form.labelSuffix}}</slot>
        </label>
        <div class="el-form-item__content" v-bind:style="contentStyle">
            <slot></slot>
            <transition name="el-zoom-in-top">
                <div
                    v-if="(validateState === 'error' || validateState === 'warning') && showMessage && form.showMessage"
                    :title="validateMessage"
                    :class="{
            'el-form-item__error--inline': typeof inlineMessage === 'boolean'
              ? inlineMessage
              : (elForm && elForm.inlineMessage || false),
              'el-form-item__error': validateState === 'error',
              'el-form-item__warning': validateState === 'warning',
          }"
                >{{validateMessage}}</div>
            </transition>
            <div
                v-if="description || $slots.description"
                class="el-form-item__description"
                :class="{
                    'is-bottom': descriptionPosition === 'bottom'
                }"
            >
                <el-tooltip v-if="descriptionPosition === 'tooltip'">
                    <i class="el-icon-info"></i>
                    <div slot="content">
                        <slot name="description">
                            {{description}}
                        </slot>
                    </div>
                </el-tooltip>
                <span v-else>
                    <slot name="description">
                        {{description}}
                    </slot>
                </span>
            </div>
        </div>
    </div>
</template>
<script>
import AsyncValidator from 'async-validator';
import emitter from 'element-ui/lib/mixins/emitter';
import objectAssign from 'element-ui/lib/utils/merge';
import { noop, getPropByPath } from 'element-ui/lib/utils/util';

export default {
    name: 'XxFormItem',

    componentName: 'ElFormItem',

    mixins: [emitter],

    provide() {
        return {
            elFormItem: this
        };
    },

    inject: ['elForm'],

    props: {
        label: String,
        labelWidth: String,
        description: String,
        descriptionPosition: {
            type: String,
            default: 'right'
        },
        prop: String,
        required: {
            type: Boolean,
            default: undefined
        },
        rules: [Object, Array],
        error: String,
        validateStatus: String,
        for: String,
        inlineMessage: {
            type: [String, Boolean],
            default: ''
        },
        showMessage: {
            type: Boolean,
            default: true
        },
        size: String,
        validateKey: {
            type: String,
            default: ''
        }
    },
    watch: {
        error: {
            immediate: true,
            handler(value) {
                this.validateMessage = value;
                this.validateState = value ? 'error' : '';
            }
        },
        validateStatus(value) {
            this.validateState = value;
        }
    },
    computed: {
        labelFor() {
            return this.for || this.prop;
        },
        labelStyle() {
            const ret = {};
            if (this.form.labelPosition === 'top') return ret;
            const labelWidth = this.labelWidth || this.form._labelWidth;
            if (labelWidth) {
                ret.width = labelWidth;
            }
            return ret;
        },
        contentStyle() {
            const ret = {};
            const label = this.label;
            if (this.$mcCream && this.$mcCream.isCreamOn) return ret;
            if (this.form.labelPosition === 'top' || this.form.inline) return ret;
            if (!label && !this.labelWidth && this.isNested) return ret;
            const labelWidth = this.labelWidth || this.form._labelWidth;
            if (labelWidth) {
                ret.marginLeft = labelWidth;
            }
            return ret;
        },
        form() {
            let parent = this.$parent;
            let parentName = parent.$options.componentName;
            while (parentName !== 'ElForm') {
                if (parentName === 'ElFormItem') {
                    this.isNested = true;
                }
                parent = parent.$parent;
                parentName = parent.$options.componentName;
            }
            return parent;
        },
        fieldValue: {
            cache: false,
            get() {
                const model = this.form.model;
                if (!model || !this.prop) {
                    return;
                }

                let path = this.prop;
                if (path.indexOf(':') !== -1) {
                    path = path.replace(/:/, '.');
                }

                return getPropByPath(model, path, true).v;
            }
        },
        isRequired() {
            let rules = this.getRules();
            let isRequired = false;

            if (rules && rules.length) {
                rules.every(rule => {
                    if (rule.required) {
                        isRequired = true;
                        return false;
                    }
                    return true;
                });
            }
            return isRequired;
        },
        _formSize() {
            return (this.elForm || {}).size;
        },
        elFormItemSize() {
            return this.size || this._formSize;
        },
        sizeClass() {
            return this.elFormItemSize || (this.$ELEMENT || {}).size;
        }
    },
    data() {
        return {
            validateState: '',
            validateMessage: '',
            validateDisabled: false,
            validator: {},
            isNested: false,
            isWeak: false,
        };
    },
    methods: {
        validate(trigger, callback = noop) {
            this.validateDisabled = false;
            const rules = this.getFilteredRule(trigger);
            if ((!rules || rules.length === 0) && this.required === undefined || !this.prop) {
                callback();
                return true;
            }
            this.validateState = 'validating';
            const descriptor = {};
            if (rules && rules.length > 0) {
                rules.forEach(rule => {
                    delete rule.trigger;
                });
            }
            this.createSchema(descriptor, rules);
            const validator = new AsyncValidator(descriptor);
            const model = {
                ...this.elForm.model
            };
            validator.validate(
                model,
                {
                    firstFields: true,
                    validLabel: this.label,
                    source: this.elForm.model,
                    extraData: this.elForm.extraData,
                    initialValue: this.initialValue,
                    vm: this.vm
                },
                (errors, invalidFields) => {
                    let failState = this.isWeak ? 'warning' : 'error';
                    this.validateState = !errors ? 'success' : failState;
                    this.validateMessage = errors ? errors[0].message : '';
                    callback(this.isWeak ? '' : this.validateMessage, invalidFields);
                    this.elForm && this.elForm.$emit('validate', this.prop, !errors || this.isWeak, this.validateMessage || null);
                }
            );
        },
        clearValidate() {
            this.validateState = '';
            this.validateMessage = '';
            this.validateDisabled = false;
        },
        resetField() {
            this.validateState = '';
            this.validateMessage = '';

            let model = this.form.model;
            let value = this.fieldValue;
            let path = this.prop;
            if (path.indexOf(':') !== -1) {
                path = path.replace(/:/, '.');
            }

            let prop = getPropByPath(model, path, true);

            this.validateDisabled = true;
            if (Array.isArray(value)) {
                prop.o[prop.k] = [].concat(this.initialValue);
            } else {
                prop.o[prop.k] = this.initialValue;
            }

            this.broadcast('ElTimeSelect', 'fieldReset', this.initialValue);
        },
        getRules() {
            let formRules = this.form.rules;
            const selfRules = this.rules;
            const requiredRule = this.required !== undefined ? { required: !!this.required } : [];

            const prop = getPropByPath(formRules, this.prop || '');
            // 处理formRules
            formRules = (formRules && prop.o[this.prop || '']) || prop.v;
            if (formRules) {
                formRules = this.formatRule(formRules);
            }
            // 处理默认规则
            const defaultRules = this.getDefaultRules();
            return [].concat(selfRules || formRules || defaultRules || []).concat(requiredRule);
        },
        initRulesValidate() {
            let rules = this.getRules();
            if (rules.length || this.required !== undefined) {
                this.$on('el.form.blur', this.onFieldBlur);
                this.$on('el.form.change', this.onFieldChange);
            }
        },
        getDefaultRules() {
            const defaultRules = this.elForm.defaultRules || {};
            const deep = !this.validateKey;
            let rule;
            let key = this.validateKey || this.prop;
            if (deep) {
                while (!rule && key) {
                    rule = defaultRules[key];
                    key = key
                        .split('.')
                        .slice(1)
                        .join('.');
                }
            } else {
                rule = defaultRules[key];
            }
            return this.formatRule(rule);
        },
        createSchema(schema, rules) {
            const arr = this.prop.split('.');
            let obj = this.elForm.model;
            arr.some((item, index) => {
                if (arr.length - 1 === index) {
                    schema[item] = rules;
                    return true;
                }
                obj = obj && obj[item];
                const type = Array.isArray(obj) ? 'array' : 'object';
                schema[item] = { type, fields: {} };
                schema = schema[item].fields;
                return false;
            });
        },
        formatRule(rule) {
            if (!rule) return;
            this.isWeak = this.isWeak || rule.isWeak;
            // rule.rules赋值是为了兼容原生的el-form的rules的非数组形式的定义
            if (!rule.rules) {
                if (Array.isArray(rule)) {
                    rule = { rules: rule };
                } else {
                    rule = { rules: [rule] };
                }
            }
            if (rule.trigger) {
                return rule.rules.map(item => {
                    item.trigger = rule.trigger;
                    return item;
                });
            }
            return rule.rules;
        },
        getFilteredRule(trigger) {
            const rules = this.getRules();
            return rules
                .map(rule => {
                    // 获取回调的栈
                    let cbStack = rule.conditionCbStack;
                    let flag = false;
                    // 根据回调判断是否需要使用验证规则
                    try {
                        if (
                            cbStack &&
                            cbStack.some(item => {
                                // 执行条件判断的回调
                                return !item(rule, this.elForm.model, this.elForm.extraData);
                            })
                        ) {
                            flag = true;
                        }
                    } catch (e) {
                        console.error(e);
                    }
                    // 回调返回false，不使用校验
                    if (flag) {
                        return {
                            validator: (rule, value, callback) => {
                                callback();
                            },
                            trigger: rule.trigger
                        };
                    }
                    return rule;
                })
                .filter(rule => {
                    if (!rule.trigger || trigger === '') return true;
                    if (Array.isArray(rule.trigger)) {
                        return rule.trigger.indexOf(trigger) > -1;
                    }
                    return rule.trigger === trigger;
                })
                .map(rule => objectAssign({}, rule));
        },
        onFieldBlur() {
            this.validate('blur');
        },
        onFieldChange() {
            if (this.validateDisabled) {
                this.validateDisabled = false;
                return;
            }

            this.validate('change');
        }
    },
    mounted() {
        if (this.prop) {
            this.dispatch('ElForm', 'el.form.addField', [this]);

            let initialValue = this.fieldValue;
            if (Array.isArray(initialValue)) {
                initialValue = [].concat(initialValue);
            }
            Object.defineProperty(this, 'initialValue', {
                value: initialValue
            });
            this.initRulesValidate();
        }
    },
    beforeDestroy() {
        this.dispatch('ElForm', 'el.form.removeField', [this]);
    }
};
</script>
<style lang="scss">
.el-form-item.is-warning .el-input__inner,
.el-form-item.is-warning .el-input__inner:focus,
.el-form-item.is-warning .el-textarea__inner,
.el-form-item.is-warning .el-textarea__inner:focus {
    border-color: #e6a23c;
}
.el-form-item.is-warning .el-input-group__append .el-input__inner,
.el-form-item.is-warning .el-input-group__prepend .el-input__inner {
    border-color: transparent;
}
.el-form-item.is-warning .el-input__validateIcon {
    color: #e6a23c;
}

.el-form-item__warning {
    color: #e6a23c;
    font-size: 12px;
    line-height: 1;
    padding-top: 4px;
    position: absolute;
    top: 100%;
    left: 0;
}
.el-form-item--small .el-form-item__warning {
    padding-top: 2px;
}
.el-form-item--mini .el-form-item__warning {
    padding-top: 1px;
}
</style>
